import { createContext, useEffect } from 'react';
import { sendNexudusLastActive } from 'ui/hooks/useNexudusLastActive';
import { inject, observer } from 'mobx-react';
import { useBWTokenContext } from './bwTokenContext';
import { DateTime } from 'luxon';
import { useMutation } from '@tanstack/react-query';

export const LAST_ACTIVE_KEY = 'nexudus:lastActiveAt';
const RATE_LIMIT_ = 30 * 60 * 1000; // 30 minutes

export const NexudusLastActiveContext = createContext(null);

export const NexudusLastActiveProvider = inject('authStore')(
  observer(({ authStore, children }) => {
    const coworkerId = authStore.customer?.Id;
    const { token } = useBWTokenContext();

    const sendIfNeededMutation = useMutation({
      mutationKey: ['lastActive', 'sendIfNeeded', coworkerId],
      mutationFn: async ([coworkerId, token]: [
        coworkerId?: number,
        token?: string | null,
      ]) => {
        // Nothing to do if we don't have the necessary data
        if (!token || !coworkerId) return;

        // Load some data from storage and see if we need to send an update
        const stored = await localStorage.getItem(LAST_ACTIVE_KEY);
        const now = DateTime.now();

        const lastActiveDateTime = stored ? DateTime.fromISO(stored) : null;
        if (!lastActiveDateTime || !lastActiveDateTime.isValid) {
          // Send a last active update RIGHT NOW
          await sendNexudusLastActive({ coworkerId, token });
          await localStorage.setItem(LAST_ACTIVE_KEY, now.toISO());
        }

        if (now.diff(lastActiveDateTime!).milliseconds >= RATE_LIMIT_) {
          await sendNexudusLastActive({ coworkerId, token });
          await localStorage.setItem(LAST_ACTIVE_KEY, now.toISO());
        }
      },
    });

    useEffect(() => {
      const interval = setInterval(async () => {
        sendIfNeededMutation.mutate([coworkerId, token]);
      }, RATE_LIMIT_);

      // Do a nice initial check immediately
      sendIfNeededMutation.mutate([coworkerId, token]);
      return () => {
        clearInterval(interval);
      };
    }, [coworkerId]);

    return (
      <NexudusLastActiveContext.Provider value={null}>
        {children}
      </NexudusLastActiveContext.Provider>
    );
  }),
);
